import { useEffect, useState } from "react";

const STORAGE_KEY = "favorites";

function readFavoriteAuctionIds() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as string[];
  } catch {
    return [];
  }
}

function writeFavoriteAuctionIds(auctionIds: string[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(auctionIds));
}

async function syncFavoriteAuctionIds(auctionIds: string[]) {
  const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
  const response = await fetch(`${api}/api/users/favorite-auctions`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ auctionIds }),
  });

  if (!response.ok) {
    throw new Error("Could not sync favorite auctions");
  }

  const data = (await response.json()) as { auctionIds?: string[] };
  return Array.isArray(data.auctionIds) ? data.auctionIds : [];
}

export function useFavoriteAuctions() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavoriteAuctionIds());

  useEffect(() => {
    const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
    const localFavorites = readFavoriteAuctionIds();

    const loadFavorites = async () => {
      try {
        const response = await fetch(`${api}/api/users/favorite-auctions`, {
          credentials: "include",
        });

        if (!response.ok) {
          return;
        }

        const data = (await response.json()) as { auctionIds?: string[] };
        const serverFavorites = Array.isArray(data.auctionIds) ? data.auctionIds : [];
        const mergedFavorites = [...new Set([...serverFavorites, ...localFavorites])];

        setFavorites(mergedFavorites);
        writeFavoriteAuctionIds(mergedFavorites);

        if (mergedFavorites.length !== serverFavorites.length) {
          const syncedFavorites = await syncFavoriteAuctionIds(mergedFavorites);
          setFavorites(syncedFavorites);
          writeFavoriteAuctionIds(syncedFavorites);
        }
      } catch {
        setFavorites(localFavorites);
      }
    };

    void loadFavorites();
  }, []);

  const saveFavorites = async (auctionIds: string[]) => {
    setFavorites(auctionIds);
    writeFavoriteAuctionIds(auctionIds);

    try {
      const syncedFavorites = await syncFavoriteAuctionIds(auctionIds);
      setFavorites(syncedFavorites);
      writeFavoriteAuctionIds(syncedFavorites);
    } catch {
      // Keep the optimistic local state; the next load will retry syncing.
    }
  };

  const toggleFavorite = (auctionId: string) => {
    const nextFavorites = favorites.includes(auctionId)
      ? favorites.filter((favoriteId) => favoriteId !== auctionId)
      : [...favorites, auctionId];

    void saveFavorites(nextFavorites);
  };

  return {
    favorites,
    toggleFavorite,
  };
}
